import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Button, ButtonList, Column, Columns, DropdownButton, FlexBox, Text, } from '@dixa/lib-shared-habla';
import { lazy, PureComponent, Suspense } from 'react';
import { analytics } from 'app/utils/segment';
import { NavigatePrompt } from 'components/navigatePrompt';
import { withRouter } from 'hocs/router';
import styles from './flowEditorChrome.module.scss';
import { TitleInput } from './titleInput';
const CanvasEditor = lazy(() => import(/* webpackChunkName: "flow-canvas" */ '../flowManager/canvas'));
export class FlowEditorChromeImpl extends PureComponent {
    constructor(props) {
        super(props);
        this.handleLeave = () => {
            const { hasUnsavedChanges, goBackPath, flowType, navigate, location } = this.props;
            const { state } = location;
            analytics.flowBackToSettingsClicked({
                warned: !!hasUnsavedChanges,
                flowType,
                path: (state === null || state === void 0 ? void 0 : state.goBackPath) || goBackPath,
            });
            if (state === null || state === void 0 ? void 0 : state.shouldGoBack) {
                navigate(-1);
            }
            else {
                navigate(goBackPath);
            }
        };
        const { initialData: { name, actions, active }, } = props;
        this.state = {
            actions,
            active,
            disableZoomerControls: false,
            name,
            showFlow: true,
        };
        this.handleOnChange = this.handleOnChange.bind(this);
        this.handleActionsChange = this.handleActionsChange.bind(this);
        this.handleNameChange = this.handleNameChange.bind(this);
        this.handleSaveClick = this.handleSaveClick.bind(this);
        this.handleActivateClick = this.handleActivateClick.bind(this);
        this.handleOnActivate = this.handleOnActivate.bind(this);
        this.handleLeave = this.handleLeave.bind(this);
        this.handleDebug = this.handleDebug.bind(this);
        this.toggleZoomerControls = this.toggleZoomerControls.bind(this);
        this.handleImport = this.handleImport.bind(this);
        this.handleExport = this.handleExport.bind(this);
    }
    getFlowData() {
        const { initialData } = this.props;
        const { name, actions, active } = this.state;
        return Object.assign(Object.assign({}, initialData), { actions,
            name,
            active });
    }
    handleOnChange() {
        const { onChange } = this.props;
        onChange(this.getFlowData());
    }
    handleActionsChange(newActions) {
        this.setState({
            actions: newActions,
        }, this.handleOnChange);
    }
    handleNameChange(newName) {
        this.setState({
            name: newName,
        }, this.handleOnChange);
    }
    handleSaveClick() {
        const { onSave, flowType } = this.props;
        analytics.flowSaveClicked({
            flowType,
        });
        onSave();
    }
    handleActivateClick() {
        const { flowType } = this.props;
        analytics.flowActivateClicked({
            flowType,
        });
        this.setState({
            active: true,
        }, this.handleOnActivate);
    }
    handleOnActivate() {
        const { onActivate } = this.props;
        onActivate(this.getFlowData());
    }
    toggleZoomerControls(toggleState) {
        const { disableZoomerControls } = this.state;
        this.setState({
            disableZoomerControls: typeof toggleState === 'boolean' ? toggleState : !disableZoomerControls,
        });
    }
    handleDebug() {
        const { actions } = this.state;
        const initNode = actions.find((act) => act.nodeType === 'init');
        if (initNode) {
            const structure = this.getStructure(actions, null);
            console.log(JSON.stringify(structure, null, 2));
        }
    }
    getStructure(actions, parentId) {
        const children = actions.filter((act) => act.parentId === parentId);
        return children.map((act) => ({
            id: act.id,
            type: act.nodeType,
            connection: act.connectionLabel,
            children: this.getStructure(actions, act.id),
        }));
    }
    handleExport() {
        const copyText = JSON.stringify(this.getFlowData());
        const body = document.body;
        if (copyText && document.body) {
            const el = document.createElement('textarea');
            el.value = copyText;
            el.setAttribute('readonly', '');
            el.style.position = 'absolute';
            el.style.left = '-9999px';
            body.appendChild(el);
            el.select();
            document.execCommand('copy');
            body.removeChild(el);
        }
        window.alert('Flow copied to clipboard!');
    }
    handleImport() {
        const flowData = JSON.parse(window.prompt('Paste flow') || '');
        if (flowData && flowData.actions && flowData.actions.length) {
            this.setState({
                showFlow: false,
                actions: flowData.actions,
                name: flowData.name,
            }, () => {
                setTimeout(() => {
                    this.setState({
                        showFlow: true,
                    });
                }, 100);
            });
        }
    }
    render() {
        var _a;
        const { nodeTypes, onChange, hasUnsavedChanges, topleftButtonComponent: TopleftButtonComponent, topleftButtonComponentProps = {}, extraData, invalidNodes, errorMessage, flowType, disableTitleEdit, navigatePromptProps, location, } = this.props;
        const { active, name } = this.state;
        const showFlowError = (invalidNodes && invalidNodes.length) || errorMessage;
        const showFlowUnsaved = !showFlowError && !!hasUnsavedChanges;
        const flowData = this.getFlowData();
        const backButtonText = ((_a = location.state) === null || _a === void 0 ? void 0 : _a.backButtonText) || 'Back to settings';
        return (_jsxs(_Fragment, { children: [_jsx(NavigatePrompt, Object.assign({ when: !!hasUnsavedChanges, message: "When you leave this page, all unsaved changes to your flow will be lost." }, navigatePromptProps)), _jsx(Columns, { children: _jsxs(Column, { children: [_jsxs(Column.Row, Object.assign({ slottable: true, borderBottom: true, className: styles.header }, { children: [_jsx(Column.Slot, Object.assign({ placement: "start" }, { children: _jsxs(FlexBox, Object.assign({ alignItems: "center", gap: "300" }, { children: [_jsx(Button, Object.assign({ icon: "arrow-left", variant: "secondary", onClick: this.handleLeave }, { children: backButtonText })), !!TopleftButtonComponent && (_jsx(TopleftButtonComponent, Object.assign({}, topleftButtonComponentProps, { isFlowActive: active, extraData: extraData, onChange: onChange })))] })) })), _jsx(Column.Slot, Object.assign({ placement: "center" }, { children: disableTitleEdit ? (_jsx(Text.Heading, { children: name })) : (_jsx(TitleInput, { flowType: flowType, initialValue: name, onChange: this.handleNameChange })) })), _jsx(Column.Slot, Object.assign({ placement: "end" }, { children: _jsxs(FlexBox, Object.assign({ alignItems: "center", gap: "300" }, { children: [showFlowUnsaved && (_jsx(Badge, Object.assign({ variant: "warning", size: "medium" }, { children: "Unsaved changes" }))), showFlowError && (_jsx(Badge, Object.assign({ variant: "error", size: "medium" }, { children: errorMessage || 'Flow contains errors' }))), window.DIXA_DEBUG && (_jsx(DropdownButton, Object.assign({ buttonProps: {
                                                        variant: 'secondary',
                                                        style: { marginRight: '8px' },
                                                    }, dropdown: _jsxs(ButtonList, { children: [_jsx(ButtonList.Item, Object.assign({ icon: "bolt", onClick: this.handleDebug }, { children: "Log action tree" })), _jsx(ButtonList.Divider, {}), _jsx(ButtonList.Item, Object.assign({ icon: "bolt", onClick: this.handleImport }, { children: "Load flow" })), _jsx(ButtonList.Item, Object.assign({ icon: "bolt", onClick: this.handleExport }, { children: "Export flow" }))] }) }, { children: "DEBUG" }))), !!hasUnsavedChanges && (_jsx(Button, Object.assign({ onClick: this.handleSaveClick, variant: "primary" }, { children: "Save flow" }))), !hasUnsavedChanges && !active && (_jsx(Button, Object.assign({ variant: "primary", onClick: this.handleActivateClick }, { children: "Activate flow" }))), !hasUnsavedChanges && active && (_jsx(Button, Object.assign({ variant: "secondary", disabled: true }, { children: "Active" })))] })) }))] })), _jsx(Column.Row, Object.assign({ size: 1, noHorizontalPadding: true, noVerticalPadding: true }, { children: _jsx(Suspense, Object.assign({ fallback: null }, { children: _jsx(CanvasEditor, { flow: flowData, flowType: flowType, nodeTypes: nodeTypes, extraData: extraData, invalidNodes: invalidNodes || [], onChange: this.handleActionsChange }) })) }))] }) })] }));
    }
}
export const FlowEditorChrome = withRouter(FlowEditorChromeImpl);
